class InfiniteScroll {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      threshold: 0.1,
      rootMargin: '0px 0px 400px 0px',
      delayMs: 100,
      ...options
    };

    this.isLoading = false;
    this.hasError = false;
    this.isFinished = false;
    this.loadTimeout = null;

    this.observer = null;
    this.sentinel = null;

    this.bindElements();
    this.init();
  }

  bindElements() {
    const parent = this.container.parentElement;
    this.statusElement = parent?.querySelector('.infinite-scroll-status');
    this.loadingElement = this.statusElement?.querySelector('.infinite-scroll-loading');
    this.finishedElement = this.statusElement?.querySelector('.infinite-scroll-finished');
    this.errorElement = this.statusElement?.querySelector('.infinite-scroll-error');
    this.nextLinkElement = parent?.querySelector('.infinite-scroll-next-link');
  }

  init() {
    this.isLoading = false;
    this.hasError = false;
    this.isFinished = false;

    if (!this.nextLinkElement) {
      this.hideStatus();
      return;
    }

    this.createSentinel();
    this.setupObserver();
    this.observeSentinel();
    this.checkSentinel();
  }

  refresh() {
    this.unobserve();
    if (this.sentinel) {
      this.sentinel.remove();
      this.sentinel = null;
    }
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.bindElements();
    this.init();
  }

  createSentinel() {
    this.sentinel = document.createElement('div');
    this.sentinel.className = 'infinite-scroll-sentinel';
    this.sentinel.style.cssText = 'height: 1px; width: 100%; visibility: hidden;';
    this.positionSentinel();
  }

  positionSentinel() {
    if (this.sentinel && this.statusElement) {
      if (this.sentinel.parentElement) {
        this.sentinel.parentElement.removeChild(this.sentinel);
      }
      this.container.parentElement.insertBefore(this.sentinel, this.statusElement);
      this.reobserveSentinel();
    }
  }

  reobserveSentinel() {
    if (this.observer && this.sentinel) {
      this.observer.unobserve(this.sentinel);
      requestAnimationFrame(() => {
        if (this.observer && this.sentinel) {
          this.observer.observe(this.sentinel);
          this.checkSentinel();
        }
      });
    }
  }

  checkSentinel() {
    if (!this.sentinel || this.isLoading || this.hasError || this.isFinished || !this.nextLinkElement) {
      return;
    }

    const rect = this.sentinel.getBoundingClientRect();
    const margin = 400;
    const isNearViewport = rect.top <= window.innerHeight + margin;

    if (isNearViewport) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = setTimeout(() => {
        this.loadMore();
      }, this.options.delayMs);
    }
  }

  setupObserver() {
    if (typeof window.IntersectionObserver === 'undefined') {
      console.warn('IntersectionObserver not supported, falling back to pagination');
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry.isIntersecting && !this.isLoading && !this.hasError && !this.isFinished) {
        clearTimeout(this.loadTimeout);
        this.loadTimeout = setTimeout(() => {
          this.loadMore();
        }, this.options.delayMs);
      }
    }, {
      threshold: [0, 0.25, 0.5, 0.75, 1],
      rootMargin: this.options.rootMargin
    });
  }

  observeSentinel() {
    if (this.observer && this.sentinel) {
      this.observer.observe(this.sentinel);
    }
  }

  async loadMore() {
    if (this.isLoading || this.hasError || this.isFinished || !this.nextLinkElement) {
      return;
    }

    if (!this.nextLinkElement.href) {
      console.warn('Next link element or href is missing');
      this.showFinished();
      return;
    }

    this.isLoading = true;
    this.showLoading();

    try {
      await new Promise(resolve => setTimeout(resolve, this.options.delayMs));

      const response = await fetch(this.nextLinkElement.href);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const listContainer = doc.querySelector('.infinite-scroll-container');
      const newProducts = listContainer
        ? listContainer.querySelectorAll('.product-card')
        : doc.querySelectorAll('product-list .product-card');
      const newNextLink = doc.querySelector('.infinite-scroll-next-link');

      if (newProducts.length === 0) {
        this.isLoading = false;
        this.showFinished();
        return;
      }

      this.appendProducts(newProducts);
      this.updateNextLink(newNextLink);

      this.isLoading = false;

      if (!newNextLink) {
        this.showFinished();
      } else {
        this.hideStatus();
        this.checkSentinel();
      }

      this.dispatchEvent('append', {
        products: newProducts,
        url: this.nextLinkElement ? this.nextLinkElement.href : ''
      });

    } catch (error) {
      console.error('Infinite scroll error:', error);
      this.hasError = true;
      this.isLoading = false;
      this.showError();
    }
  }

  appendProducts(products) {
    const fragment = document.createDocumentFragment();

    products.forEach(product => {
      const clonedProduct = product.cloneNode(true);
      this.processNewProduct(clonedProduct);
      fragment.appendChild(clonedProduct);
    });

    this.container.appendChild(fragment);

    requestAnimationFrame(() => {
      this.positionSentinel();
    });
  }

  processNewProduct(product) {
    const images = product.querySelectorAll('img[loading="lazy"]');
    images.forEach(img => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        delete img.dataset.src;
      }
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        delete img.dataset.srcset;
      }
    });

    const customElements = product.querySelectorAll('[class*="product-"]');
    customElements.forEach(el => {
      if (!el.hasAttribute('data-initialized')) {
        el.setAttribute('data-initialized', 'true');
        el.dispatchEvent(new CustomEvent('element:loaded', { bubbles: true }));
      }
    });
  }

  updateNextLink(newNextLink) {
    if (newNextLink && newNextLink.href) {
      if (this.nextLinkElement) {
        this.nextLinkElement.href = newNextLink.href;
      }
    } else {
      this.nextLinkElement = null;
      this.isFinished = true;
    }
  }

  showLoading() {
    this.hideStatus();
    this.statusElement?.classList.add('is-loading');
  }

  showFinished() {
    this.hideStatus();
    this.isFinished = true;
    this.statusElement?.classList.add('is-finished');
    this.unobserve();
  }

  showError() {
    this.hideStatus();
    this.statusElement?.classList.add('is-error');
  }

  hideStatus() {
    this.statusElement?.classList.remove('is-loading', 'is-finished', 'is-error');
  }

  unobserve() {
    if (this.observer && this.sentinel) {
      this.observer.unobserve(this.sentinel);
    }
  }

  dispatchEvent(type, detail) {
    const event = new CustomEvent(`infiniteScroll:${type}`, {
      detail,
      bubbles: true
    });
    this.container.dispatchEvent(event);
  }

  destroy() {
    this.unobserve();
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.sentinel) {
      this.sentinel.remove();
      this.sentinel = null;
    }
    this.hideStatus();
  }
}

function initInfiniteScroll() {
  const container = document.querySelector('.infinite-scroll-container');
  if (!container) return null;

  if (window.themeInfiniteScroll) {
    window.themeInfiniteScroll.destroy();
    window.themeInfiniteScroll = null;
  }

  return new InfiniteScroll(container, {
    threshold: 0.1,
    rootMargin: '0px 0px 400px 0px',
    delayMs: 100
  });
}

function reinitInfiniteScroll() {
  window.themeInfiniteScroll = initInfiniteScroll();
}

function handleSectionReload(event) {
  if (event.detail.sectionId.includes('main-collection')) {
    requestAnimationFrame(() => {
      reinitInfiniteScroll();
    });
  }
}

function watchFacetUpdate() {
  document.addEventListener('facet:update', () => {
    const section = document.querySelector('.shopify-section--main-collection');
    if (!section) return;

    let reinitialized = false;

    const observer = new MutationObserver(() => {
      if (reinitialized || !document.querySelector('.infinite-scroll-container')) return;
      reinitialized = true;
      observer.disconnect();
      requestAnimationFrame(() => {
        reinitInfiniteScroll();
      });
    });

    observer.observe(section, { childList: true, subtree: true });

    setTimeout(() => {
      if (!reinitialized && document.querySelector('.infinite-scroll-container')) {
        reinitialized = true;
        observer.disconnect();
        reinitInfiniteScroll();
      }
    }, 1000);
  });
}

function safeInit() {
  if (document.querySelector('.infinite-scroll-container')) {
    window.themeInfiniteScroll = initInfiniteScroll();
  } else {
    setTimeout(safeInit, 100);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', safeInit);
} else if (document.readyState === 'interactive') {
  setTimeout(safeInit, 0);
} else {
  safeInit();
}

window.addEventListener('load', () => {
  if (!window.themeInfiniteScroll) {
    safeInit();
  }
});

watchFacetUpdate();
document.addEventListener('shopify:section:load', handleSectionReload);
document.addEventListener('shopify:section:reorder', handleSectionReload);
